import React from 'react';

/**
 * Pagination Component
 * Reusable pagination for list views
 */
const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems = null,
  itemsPerPage = null,
  showInfo = true,
  className = ''
}) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisiblePages = 5;
  
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const buttonClass = (isActive = false, isDisabled = false) => `
    px-3 py-1 mx-1 rounded text-sm font-medium transition-colors
    ${isDisabled 
      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
      : isActive 
        ? 'bg-blue-500 text-white' 
        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}
  `;

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {showInfo && totalItems !== null && itemsPerPage !== null && (
        <div className="text-sm text-gray-600">
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{' '}
          {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
        </div>
      )}
      
      <div className="flex items-center">
        {/* First Page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className={buttonClass(false, currentPage === 1)}
          title="First page"
        >
          &laquo;
        </button>
        
        {/* Previous Page */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={buttonClass(false, currentPage === 1)}
          title="Previous page"
        >
          &lsaquo;
        </button>

        {/* Page Numbers */}
        {startPage > 1 && (
          <>
            <button onClick={() => onPageChange(1)} className={buttonClass()}>
              1
            </button>
            {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
          </>
        )}
        
        {pages.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={buttonClass(page === currentPage)}
          >
            {page}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2 text-gray-500">...</span>}
            <button onClick={() => onPageChange(totalPages)} className={buttonClass()}>
              {totalPages}
            </button>
          </>
        )}

        {/* Next Page */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={buttonClass(false, currentPage === totalPages)}
          title="Next page"
        >
          &rsaquo;
        </button>
        
        {/* Last Page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={buttonClass(false, currentPage === totalPages)}
          title="Last page"
        >
          &raquo;
        </button>
      </div>
    </div>
  );
};

export default Pagination;

